import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Globe,
  Code,
  Handshake,
  TrendingUp,
  Users,
  Zap,
  Monitor,
  BarChart3,
  Layers,
  ChevronDown,
  Mail,
  MapPin,
} from 'lucide-react';

const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-midnight-900/95 backdrop-blur-xl shadow-lg shadow-black/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-tiki-400 to-tiki-600 flex items-center justify-center shadow-lg shadow-tiki-500/25 group-hover:shadow-tiki-500/40 transition-shadow">
              <span className="text-white font-bold text-lg">T</span>
            </div>
            <span className="text-white font-bold text-xl tracking-tight">
              Tiki<span className="text-tiki-400">Media</span>
            </span>
          </a>
          <div className="hidden md:flex items-center gap-10">
            <a href="#about" className="text-sm text-midnight-300 hover:text-white transition-colors">
              About
            </a>
            <a href="#services" className="text-sm text-midnight-300 hover:text-white transition-colors">
              Services
            </a>
            <a href="#approach" className="text-sm text-midnight-300 hover:text-white transition-colors">
              Approach
            </a>
            <a
              href="#contact"
              className="text-sm font-medium text-midnight-900 bg-tiki-400 hover:bg-tiki-300 px-5 py-2.5 rounded-full transition-all hover:shadow-lg hover:shadow-tiki-400/25"
            >
              Let's Talk
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center gradient-bg noise-overlay overflow-hidden">
      {/* Abstract background shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-tiki-500/10 blur-3xl animate-pulse-slow" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-tiki-600/8 blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-tiki-700/5 blur-3xl" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 w-full">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-tiki-300 text-sm font-medium mb-8 animate-fade-in">
            <Zap className="w-4 h-4" />
            Advertising & Technology Partner
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold text-white leading-[0.95] tracking-tight mb-8 animate-fade-in-up">
            Where creativity
            <br />
            meets{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-tiki-300 via-tiki-400 to-tiki-500">
              innovation
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-midnight-300 max-w-2xl leading-relaxed mb-12 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Tiki Media is your dynamic partner in the world of advertising and
            cutting-edge technologies. We connect brands with audiences through
            data-driven strategies and creative excellence.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <a
              href="#services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-tiki-500 hover:bg-tiki-400 text-midnight-950 font-semibold rounded-full transition-all hover:shadow-xl hover:shadow-tiki-500/25 hover:-translate-y-0.5"
            >
              Explore Our Services
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 glass text-white font-medium rounded-full hover:bg-white/10 transition-all"
            >
              Get in Touch
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-midnight-400 animate-bounce">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <ChevronDown className="w-5 h-5" />
        </div>
      </div>
    </section>
  );
};

const About = () => {
  const { ref, inView } = useInView();

  return (
    <section id="about" className="relative py-32 bg-white overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-midnight-200 to-transparent" />
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <span className="inline-block text-sm font-semibold text-tiki-600 tracking-widest uppercase mb-4">
              About Us
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-midnight-900 leading-tight mb-6">
              A team built on{' '}
              <span className="gradient-text">ambition & expertise</span>
            </h2>
            <p className="text-lg text-midnight-500 leading-relaxed mb-6">
              Tiki Media was founded with a clear mission: to bridge the gap between
              creative storytelling and technological innovation. We are a team of
              marketers, developers, and strategists who thrive at the intersection
              of advertising and technology.
            </p>
            <p className="text-lg text-midnight-500 leading-relaxed">
              Based in Tel Aviv, we bring a global perspective to every project,
              helping businesses scale through smart partnerships, optimized media
              buying, and custom-built software solutions that deliver measurable results.
            </p>
          </div>

          <div className={`transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="grid grid-cols-2 gap-5">
              {[
                { number: '50+', label: 'Active Partners', icon: Users },
                { number: '12+', label: 'Markets Reached', icon: Globe },
                { number: '99%', label: 'Client Retention', icon: TrendingUp },
                { number: '24/7', label: 'Campaign Monitoring', icon: Monitor },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="group relative bg-midnight-50 rounded-2xl p-6 hover:bg-midnight-900 transition-all duration-500 cursor-default"
                >
                  <stat.icon className="w-6 h-6 text-tiki-500 mb-4 group-hover:text-tiki-400 transition-colors" />
                  <div className="text-3xl font-bold text-midnight-900 group-hover:text-white transition-colors mb-1">
                    {stat.number}
                  </div>
                  <div className="text-sm text-midnight-500 group-hover:text-midnight-300 transition-colors">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const { ref, inView } = useInView();

  const services = [
    {
      icon: Handshake,
      title: 'Business Development',
      description:
        'We leverage our extensive network to connect you with the right customers, affiliates, and networks. Our partnership-first approach opens doors to high-value opportunities that accelerate your growth.',
      highlights: ['Strategic partnerships', 'Affiliate network access', 'Growth consulting'],
    },
    {
      icon: BarChart3,
      title: 'Online Media',
      description:
        'From campaign setup to real-time monitoring and optimization, our ad operations team ensures every impression counts. We identify the best affiliate offers and manage campaigns for maximum ROI.',
      highlights: ['Ad operations', 'Campaign management', 'Affiliate offer identification'],
    },
    {
      icon: Code,
      title: 'Software Solutions',
      description:
        'Our development team delivers custom native and web applications built to perform. From intuitive UIs to robust backend systems, we write clean, scalable code that powers your business.',
      highlights: ['Native & web apps', 'UI/UX development', 'Backend engineering'],
    },
  ];

  return (
    <section id="services" className="relative py-32 bg-midnight-50">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-sm font-semibold text-tiki-600 tracking-widest uppercase mb-4">
            What We Do
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-midnight-900 leading-tight mb-6">
            Services designed to{' '}
            <span className="gradient-text">drive results</span>
          </h2>
          <p className="text-lg text-midnight-500">
            We combine industry expertise with innovative technology to deliver end-to-end
            solutions that move your business forward.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, i) => (
            <div
              key={i}
              className={`group relative bg-white rounded-3xl p-8 lg:p-10 shadow-sm hover:shadow-2xl hover:shadow-midnight-900/5 transition-all duration-500 hover:-translate-y-1 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-tiki-400 to-tiki-600 flex items-center justify-center mb-6 shadow-lg shadow-tiki-500/20 group-hover:scale-110 transition-transform">
                <service.icon className="w-7 h-7 text-white" />
              </div>

              <h3 className="text-xl font-bold text-midnight-900 mb-4">
                {service.title}
              </h3>

              <p className="text-midnight-500 leading-relaxed mb-6">
                {service.description}
              </p>

              <ul className="space-y-2">
                {service.highlights.map((item, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-midnight-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-tiki-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Approach = () => {
  const { ref, inView } = useInView();

  const steps = [
    {
      step: '01',
      title: 'Discover',
      description: 'We dig deep into your business, audience, and goals to build a complete understanding of where you are and where you want to be.',
    },
    {
      step: '02',
      title: 'Strategize',
      description: 'Our team crafts a tailored plan that aligns creative concepts with data-driven media strategies for maximum impact.',
    },
    {
      step: '03',
      title: 'Execute',
      description: 'We launch, monitor, and optimize in real time — adjusting campaigns, partnerships, and code to ensure peak performance.',
    },
    {
      step: '04',
      title: 'Scale',
      description: 'Once we find what works, we double down. We expand winning strategies across channels and markets to multiply your results.',
    },
  ];

  return (
    <section id="approach" className="relative py-32 bg-white overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-sm font-semibold text-tiki-600 tracking-widest uppercase mb-4">
            Our Approach
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-midnight-900 leading-tight mb-6">
            From concept to{' '}
            <span className="gradient-text">scale</span>
          </h2>
          <p className="text-lg text-midnight-500">
            A proven process that turns ideas into measurable outcomes.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((item, i) => (
            <div
              key={i}
              className={`relative transition-all duration-700 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="text-7xl font-black text-midnight-100 mb-4 leading-none select-none">
                {item.step}
              </div>
              <h3 className="text-xl font-bold text-midnight-900 mb-3">
                {item.title}
              </h3>
              <p className="text-midnight-500 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyUs = () => {
  const { ref, inView } = useInView();

  const features = [
    {
      icon: Layers,
      title: 'Full-Stack Capabilities',
      description: 'From media buying to custom development, we handle every layer of your digital presence.',
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Our network spans multiple markets, giving your campaigns international reach with local precision.',
    },
    {
      icon: Zap,
      title: 'Speed & Agility',
      description: 'We move fast. Quick iteration cycles and real-time optimization keep you ahead of the competition.',
    },
    {
      icon: TrendingUp,
      title: 'Performance-Driven',
      description: 'Every decision is backed by data. We focus on metrics that matter and strategies that deliver ROI.',
    },
  ];

  return (
    <section className="relative py-32 gradient-bg noise-overlay overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-tiki-500/8 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-tiki-600/5 blur-3xl" />
      </div>

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-sm font-semibold text-tiki-400 tracking-widest uppercase mb-4">
            Why Tiki Media
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Built different,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-tiki-300 to-tiki-500">
              built to last
            </span>
          </h2>
          <p className="text-lg text-midnight-300">
            We're not just another agency. We're a technology-powered partner
            invested in your long-term success.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {features.map((feature, i) => (
            <div
              key={i}
              className={`glass rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <feature.icon className="w-8 h-8 text-tiki-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-midnight-300 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const { ref, inView } = useInView();

  return (
    <section id="contact" className="relative py-32 bg-midnight-50 overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`max-w-3xl mx-auto text-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-sm font-semibold text-tiki-600 tracking-widest uppercase mb-4">
            Get in Touch
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-midnight-900 leading-tight mb-6">
            Ready to grow?{' '}
            <span className="gradient-text">Let's talk.</span>
          </h2>
          <p className="text-lg text-midnight-500 mb-12">
            Whether you're looking to scale your campaigns, build custom software,
            or explore new partnerships, we'd love to hear from you.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-16">
            <a
              href="mailto:contact@tiki-m.com"
              className="inline-flex items-center gap-3 px-8 py-4 bg-midnight-900 hover:bg-midnight-800 text-white font-semibold rounded-full transition-all hover:shadow-xl hover:shadow-midnight-900/20 hover:-translate-y-0.5"
            >
              <Mail className="w-5 h-5" />
              contact@tiki-m.com
            </a>
          </div>

          <div className="flex items-center justify-center gap-2 text-midnight-400">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">Baal Haakeda 27 A, Tel Aviv-Jaffa, Israel</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-midnight-950 border-t border-midnight-800/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-tiki-400 to-tiki-600 flex items-center justify-center">
              <span className="text-white font-bold text-xs">T</span>
            </div>
            <span className="text-midnight-400 text-sm">
              &copy; {new Date().getFullYear()} Tiki Media. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-sm text-midnight-400 hover:text-tiki-400 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-sm text-midnight-400 hover:text-tiki-400 transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const HomePage = () => {
  return (
    <div className="overflow-hidden">
      <Navbar />
      <Hero />
      <About />
      <Services />
      <Approach />
      <WhyUs />
      <Contact />
      <Footer />
    </div>
  );
};

export default HomePage;
